// oxlint-disable-next-line typescript/no-explicit-any -- for Errors that may not have a specific type
type ANYTHING = any

/**
 * Represents a successful result with data
 */
type Success<T> = {
	data: T
	error: null
	isSuccess: true
}

/**
 * Represents a failed result with error
 */
type Failure<E> = {
	data: null
	error: E
	isSuccess: false
}

/**
 * Union type representing either success or failure
 */
type Result<T, E = Error> = Success<T> | Failure<E>

/**
 * Standard HTTP status code messages
 */
const HTTP_STATUS_MESSAGES: Record<number, string> = {
	400: 'Bad Request',
	401: 'Unauthorized',
	403: 'Forbidden',
	404: 'Not Found',
	405: 'Method Not Allowed',
	408: 'Request Timeout',
	409: 'Conflict',
	429: 'Too Many Requests',
	500: 'Internal Server Error',
	502: 'Bad Gateway',
	503: 'Service Unavailable',
	504: 'Gateway Timeout',
}

/**
 * Creates a success result
 */
const success = <T>(data: T): Success<T> => ({ data, error: null, isSuccess: true })

const statusOf = (source: ANYTHING): number | undefined => source?.response?.status || source?.status

const messageFor = (message: string | undefined, statusCode: number): string =>
	message || HTTP_STATUS_MESSAGES[statusCode] || 'Unknown Error'

const failed = <E>(error: E): Failure<E> => ({ data: null, error, isSuccess: false })

/**
 * Creates a failure result
 */
const failure = <E extends ErrorResponse>(error: E | unknown): Failure<E> => {
	if (error instanceof Error) {
		const statusCode = statusOf(error) || 500

		// The thrown message wins: falling back to the status text first turned
		// every non-HTTP error into "Internal Server Error".
		return failed({
			code: statusCode,
			message: messageFor(error.message, statusCode),
			originalError: error,
			stack: error.stack || new Error().stack,
		} as unknown as E)
	}

	if (typeof error === 'object' && error !== null) {
		const given = error as E
		const statusCode = given.code || statusOf(given.originalError) || 500

		return failed({
			...given,
			code: statusCode,
			message: messageFor(given.message, statusCode),
			stack: new Error().stack,
			originalError: given.originalError || given,
		})
	}

	return failed({
		code: 500,
		message: HTTP_STATUS_MESSAGES[500] || String(error),
		originalError: error,
		stack: new Error().stack,
	} as unknown as E)
}

/**
 * Standard API error interface
 */
interface ErrorResponse {
	code?: number
	message: string
	stack?: string
	originalError?: Error | unknown
}

/**
 * Wraps a promise to handle success and error cases in a type-safe way
 *
 * @param promise - The promise to wrap
 * @returns A Result object containing either data or error
 *
 * @example
 * const result = await tryCatch(fetchUserData(userId));
 * if (result.error) {
 *   console.error('Failed:', result.error);
 *   return;
 * }
 * console.log('User:', result.data);
 * ```
 */
const tryCatch = async <T, E extends ErrorResponse>(promise: Promise<T>): Promise<Result<T, E>> => {
	try {
		return success(await promise)
	} catch (error) {
		return failure<E>(error)
	}
}

/**
 * Maps a successful Result to a new Result
 */
const map = <T, U, E extends ErrorResponse>(result: Result<T, E>, fn: (value: T) => U): Result<U, E> =>
	result.isSuccess ? success(fn(result.data)) : failure<E>(result.error)

/**
 * Chains multiple Results together
 */
const flatMap = <T, U, E extends ErrorResponse>(result: Result<T, E>, fn: (value: T) => Result<U, E>): Result<U, E> =>
	result.isSuccess ? fn(result.data) : failure<E>(result.error)

export { failure, flatMap, map, success, tryCatch }
export type { ErrorResponse, Failure, Result, Success }
